import { useCallback, useRef, useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import {
  ServiceType,
  type MenuVendorLocationWithDeliveryRegionsFragment,
  type UserBasketSettingsLocationFragment,
} from '@/generated/graphql'
import { getCountryCodeFromName } from '@/helpers/multiRegion/countries'
import { basketLocationToAddress } from '@caterdesk/utils--gm-validation'
import { GET_VENDORS_IN_RANGE_LOCATIONS } from '../components/input-bar/useVendorInRangeLocations'
import { useVendorLocationActions } from '../store/vendorLocationStore'
import { GetVendorsInRangeQuery } from '@/generated/gql/graphql'

export type InRangeLocations = GetVendorsInRangeQuery['vendor']['inRangeLocations']

export const hasDeliveryRegionCoverage = (
  location: GetVendorsInRangeQuery['vendor']['inRangeLocations'][0],
): boolean => Boolean(location.deliveryRegions?.filter(Boolean).length)

type ValidateOptions = { headCount?: number | null }
type ValidateResult = {
  ok: boolean
  locations: InRangeLocations
}

export const useAddressInRangeCheck = ({ vendorId }: { vendorId?: string }) => {
  const [runQuery, { loading: loadingInRange, error: inRangeError }] = useLazyQuery(
    GET_VENDORS_IN_RANGE_LOCATIONS,
  )

  const { setInRangeVendorLocations } = useVendorLocationActions()
  const validateLocationInRange = useCallback(
    async (
      location: UserBasketSettingsLocationFragment,
      options?: ValidateOptions,
    ): Promise<ValidateResult> => {
      if (!vendorId) {
        setInRangeVendorLocations([])
        return { ok: false, locations: [] }
      }

      const { latitude, longitude, country } = basketLocationToAddress(location)

      if (!latitude || !longitude) {
        setInRangeVendorLocations([])
        return { ok: false, locations: [] }
      }

      const countryCode = getCountryCodeFromName(country) || null

      try {
        const res = await runQuery({
          variables: {
            vendorId,
            lat: Number(latitude),
            lng: Number(longitude),
            serviceType: ServiceType.Gm,
            countryCode,
            dateTime: null,
            headCount: options?.headCount ?? null,
          },
          fetchPolicy: 'network-only',
        })

        const deliverableLocations =
          res.data?.vendor?.inRangeLocations?.filter(hasDeliveryRegionCoverage) ?? []

        setInRangeVendorLocations(deliverableLocations)
        return { ok: deliverableLocations.length > 0, locations: deliverableLocations }
      } catch (e) {
        setInRangeVendorLocations([])
        return { ok: false, locations: [] }
      }
    },
    [runQuery, vendorId],
  )

  return {
    validateLocationInRange,
    loadingInRange,
    inRangeError,
  }
}
